import { useState, useMemo, useCallback, useEffect } from 'react';
import { complianceApi } from '@/api/compliance.api';
import { useWarehouseStore } from '@/stores/warehouseStore';

function formatINR(n?: number | string | null): string {
  const num = Number(n);
  if (n == null || isNaN(num)) return '₹0.00';
  return `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return String(iso);
  }
}

interface BulkRegisterModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

type Step = 1 | 2 | 3;

interface GenerateResult {
  invoiceId: string;
  invoice: string;
  customer: string;
  status: 'SUCCESS' | 'FAILED';
  irn: string;
  ackNumber?: string;
  error: string;
}

export default function BulkRegisterModal({ onClose, onSuccess }: BulkRegisterModalProps) {
  const [step, setStep] = useState<Step>(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dateFrom, setDateFrom] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [results, setResults] = useState<GenerateResult[]>([]);
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);
  const [realInvoices, setRealInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const { selectedWarehouseId, warehouses } = useWarehouseStore();

  const activeWarehouseName = useMemo(() => {
    if (!selectedWarehouseId || selectedWarehouseId === 'ALL') return null;
    return (warehouses || []).find((w) => w.id === selectedWarehouseId)?.name || null;
  }, [selectedWarehouseId, warehouses]);

  const loadPendingInvoices = useCallback(async () => {
    setLoadingInvoices(true);
    try {
      const res = await complianceApi.searchInvoices('', selectedWarehouseId, false, true, true);
      const items = res?.data?.items || res?.data || res || [];
      if (Array.isArray(items)) {
        setRealInvoices(items);
      }
    } catch (err) {
      console.error('Failed to load pending invoices for bulk registration:', err);
    } finally {
      setLoadingInvoices(false);
    }
  }, [selectedWarehouseId]);

  useEffect(() => {
    loadPendingInvoices();
  }, [loadPendingInvoices]);

  // B2B invoices without IRN
  const eligibleInvoices = useMemo(() => {
    return realInvoices.map((inv) => ({
      id: inv.id,
      billNo: inv.invoice_number || inv.billNo || inv.invoiceNo || 'INV',
      partyName: inv.customer_name || inv.partyName || inv.name || 'Customer / Buyer',
      customerGstin: (inv.customer_gstin || inv.customerGstin || inv.gstin || '').trim(),
      date: inv.invoice_date || inv.date || '',
      grandTotal: Number(inv.total_amount || inv.grandTotal || 0),
      itemCount: Number(inv.item_count || inv.itemCount || (inv.items ? inv.items.length : 1)),
      billingAddress: inv.billing_address || inv.customer_address || '',
    })).filter((si) => {
      // Must have valid 15-digit GSTIN for B2B E-Invoicing
      return si.customerGstin.length === 15;
    });
  }, [realInvoices]);

  const filteredInvoices = useMemo(() => {
    let list = eligibleInvoices;
    if (dateFrom) {
      list = list.filter((si) => si.date >= dateFrom);
    }
    if (customerSearch.trim()) {
      const q = customerSearch.toLowerCase().trim();
      list = list.filter(
        (si) =>
          si.partyName.toLowerCase().includes(q) ||
          si.billNo.toLowerCase().includes(q) ||
          si.customerGstin.toLowerCase().includes(q)
      );
    }
    return list;
  }, [eligibleInvoices, dateFrom, customerSearch]);

  const allSelected = filteredInvoices.length > 0 && selected.size === filteredInvoices.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredInvoices.map((si) => si.id || si.billNo)));
    }
  };

  const toggleOne = (idOrBillNo: string) => {
    const next = new Set(selected);
    if (next.has(idOrBillNo)) {
      next.delete(idOrBillNo);
    } else {
      next.add(idOrBillNo);
    }
    setSelected(next);
  };

  const selectedInvoices = useMemo(() => {
    return filteredInvoices.filter((si) => selected.has(si.id) || selected.has(si.billNo));
  }, [filteredInvoices, selected]);

  const totalValue = selectedInvoices.reduce((s, si) => s + si.grandTotal, 0);

  // Missing GSTIN check
  const validSelected = useMemo(() => {
    return selectedInvoices.filter((si) => !!si.customerGstin && si.customerGstin.length === 15);
  }, [selectedInvoices]);

  const missingGstin = useMemo(() => {
    return selectedInvoices.filter((si) => !si.customerGstin || si.customerGstin.length !== 15);
  }, [selectedInvoices]);

  const handleGenerateAll = useCallback(() => {
    const toGenerate = validSelected;
    if (toGenerate.length === 0) return;

    setGenerating(true);
    setProgress({ current: 0, total: toGenerate.length });
    setResults([]);

    const genResults: GenerateResult[] = [];

    const processNext = async (idx: number) => {
      if (idx >= toGenerate.length) {
        setGenerating(false);
        setDone(true);
        setResults(genResults);
        onSuccess?.();
        return;
      }

      const si = toGenerate[idx];
      try {
        const res = await complianceApi.generateEInvoice({
          invoiceId: si.id,
          invoiceNumber: si.billNo,
        });

        if (res?.success && res?.data) {
          genResults.push({
            invoiceId: si.id,
            invoice: si.billNo,
            customer: si.partyName,
            status: 'SUCCESS',
            irn: res.data.irn || '',
            ackNumber: res.data.ackNumber || res.data.ack_no || '',
            error: '',
          });
        } else {
          genResults.push({
            invoiceId: si.id,
            invoice: si.billNo,
            customer: si.partyName,
            status: 'FAILED',
            irn: '',
            error: res?.message || 'IRP registration failed',
          });
        }
      } catch (err: any) {
        genResults.push({
          invoiceId: si.id,
          invoice: si.billNo,
          customer: si.partyName,
          status: 'FAILED',
          irn: '',
          error: err?.response?.data?.message || err?.message || 'Network error connecting to IRP',
        });
      }

      setProgress({ current: idx + 1, total: toGenerate.length });
      setTimeout(() => processNext(idx + 1), 350);
    };

    processNext(0);
  }, [validSelected, onSuccess]);

  const successCount = results.filter((r) => r.status === 'SUCCESS').length;
  const failedCount = results.filter((r) => r.status === 'FAILED').length;

  const handleCopyIrn = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text).catch(() => {});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-xs" onClick={!generating ? onClose : undefined} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden border border-gray-100">
        {/* Header */}
        <div className="shrink-0 px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[#4f46e5]">
              <i className="ri-stack-line text-lg" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-900">Bulk IRN Registration</h2>
                {activeWarehouseName && (
                  <span className="text-[10px] bg-slate-100 text-slate-700 font-medium px-2 py-0.5 rounded-md border border-slate-200">
                    {activeWarehouseName}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500">Batch generate e-invoices with Government IRP portal</p>
            </div>
          </div>
          <button
            onClick={!generating ? onClose : undefined}
            disabled={generating}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors disabled:opacity-30 cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Step indicators */}
        <div className="shrink-0 px-6 py-3 bg-slate-50 border-b border-gray-200">
          <div className="flex items-center justify-between max-w-lg mx-auto">
            {([1, 2, 3] as Step[]).map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                    s < step
                      ? 'bg-emerald-600 text-white shadow-xs'
                      : s === step
                      ? 'bg-[#4f46e5] text-white ring-4 ring-indigo-100 shadow-xs'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {s < step ? <i className="ri-check-line text-sm" /> : s}
                </div>
                <span className={`text-xs font-semibold ${s === step ? 'text-indigo-600 font-bold' : s < step ? 'text-gray-800' : 'text-gray-400'}`}>
                  {s === 1 ? 'Select Invoices' : s === 2 ? 'Review & Validate' : 'Register with IRP'}
                </span>
                {i < 2 && <div className={`w-8 sm:w-12 h-0.5 ${s < step ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 bg-white">
          {/* STEP 1: Select Invoices */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <div className="relative flex-1">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search by customer name, invoice #, or GSTIN..."
                    className="w-full pl-9 pr-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] bg-white shadow-xs"
                  />
                  {customerSearch && (
                    <button
                      onClick={() => setCustomerSearch('')}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <i className="ri-close-circle-fill text-sm" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    title="Filter by invoice date from"
                    className="px-3 py-2 text-xs sm:text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20 focus:border-[#4f46e5] bg-white shadow-xs"
                  />
                  {dateFrom && (
                    <button
                      onClick={() => setDateFrom('')}
                      className="p-2 text-xs text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                      title="Clear date filter"
                    >
                      <i className="ri-filter-off-line" />
                    </button>
                  )}
                  <button
                    onClick={loadPendingInvoices}
                    disabled={loadingInvoices}
                    className="p-2 text-xs text-gray-600 hover:text-indigo-600 bg-gray-100 hover:bg-indigo-50 border border-gray-200 rounded-lg transition-colors cursor-pointer"
                    title="Refresh invoices"
                  >
                    <i className={`ri-refresh-line text-sm ${loadingInvoices ? 'animate-spin' : ''}`} />
                  </button>
                </div>
              </div>

              {/* Invoices Table */}
              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                <div className="max-h-[380px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 z-10 bg-slate-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-3 w-10 text-center">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            className="w-3.5 h-3.5 rounded border-gray-300 text-[#4f46e5] focus:ring-indigo-500 cursor-pointer"
                          />
                        </th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">Invoice No</th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">Customer / Buyer</th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">GSTIN</th>
                        <th className="px-3 py-3 text-left font-bold text-gray-600 uppercase tracking-wider">Date</th>
                        <th className="px-3 py-3 text-right font-bold text-gray-600 uppercase tracking-wider">Amount</th>
                        <th className="px-3 py-3 text-center font-bold text-gray-600 uppercase tracking-wider">Items</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {loadingInvoices ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-12 text-center text-gray-400">
                            <i className="ri-loader-4-line animate-spin text-2xl text-indigo-600 mb-2 block mx-auto" />
                            Loading pending B2B sales invoices...
                          </td>
                        </tr>
                      ) : filteredInvoices.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-3 py-12 text-center text-gray-400">
                            <i className="ri-inbox-line text-3xl text-gray-300 mb-2 block mx-auto" />
                            No pending B2B invoices found for bulk E-Invoicing.
                          </td>
                        </tr>
                      ) : (
                        filteredInvoices.map((si) => {
                          const isChecked = selected.has(si.id) || selected.has(si.billNo);
                          return (
                            <tr
                              key={si.id || si.billNo}
                              onClick={() => toggleOne(si.id || si.billNo)}
                              className={`hover:bg-indigo-50/40 cursor-pointer transition-colors ${
                                isChecked ? 'bg-indigo-50/70 font-medium' : ''
                              }`}
                            >
                              <td className="px-3 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => toggleOne(si.id || si.billNo)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-[#4f46e5] focus:ring-indigo-500 cursor-pointer"
                                />
                              </td>
                              <td className="px-3 py-3 font-mono font-bold text-indigo-600 whitespace-nowrap">
                                {si.billNo}
                              </td>
                              <td className="px-3 py-3 text-gray-900 max-w-[200px] truncate">
                                {si.partyName}
                              </td>
                              <td className="px-3 py-3 font-mono text-gray-600 whitespace-nowrap">
                                {si.customerGstin || '—'}
                              </td>
                              <td className="px-3 py-3 text-gray-600 whitespace-nowrap">
                                {formatDate(si.date)}
                              </td>
                              <td className="px-3 py-3 text-right font-semibold text-gray-900 whitespace-nowrap">
                                {formatINR(si.grandTotal)}
                              </td>
                              <td className="px-3 py-3 text-center text-gray-500 whitespace-nowrap">
                                {si.itemCount}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Bottom bar */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
                <div className="text-xs text-gray-600 flex items-center gap-3">
                  <span className="font-semibold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-md">
                    {selected.size} of {filteredInvoices.length} selected
                  </span>
                  <span>
                    Total Value: <strong className="text-indigo-700">{formatINR(totalValue)}</strong>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-xs font-semibold text-gray-600 hover:text-gray-800 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setStep(2)}
                    disabled={selected.size === 0}
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-xs transition-colors"
                  >
                    Review Selection ({selected.size}) <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Review & Validate */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider">Batch Summary</h4>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Validating {validSelected.length} B2B invoice payloads for GST IRP registration.
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold text-indigo-600">{formatINR(totalValue)}</div>
                  <div className="text-[11px] text-gray-500">{validSelected.length} Invoices Ready</div>
                </div>
              </div>

              {missingGstin.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5">
                  <div className="flex items-center gap-2 text-amber-800 font-bold text-xs mb-1">
                    <i className="ri-alert-line text-sm text-amber-600" />
                    <span>{missingGstin.length} invoice(s) excluded due to invalid GSTIN</span>
                  </div>
                  <div className="space-y-1 max-h-24 overflow-y-auto mt-2">
                    {missingGstin.map((si) => (
                      <div key={si.id} className="text-xs text-amber-700 flex items-center justify-between">
                        <span>{si.billNo} — {si.partyName}</span>
                        <span className="font-mono text-[10px] text-red-600">Missing 15-char GSTIN</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                <div className="max-h-[350px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2.5 text-left font-bold text-gray-600">Invoice No</th>
                        <th className="px-3 py-2.5 text-left font-bold text-gray-600">Customer</th>
                        <th className="px-3 py-2.5 text-left font-bold text-gray-600">GSTIN</th>
                        <th className="px-3 py-2.5 text-left font-bold text-gray-600">Date</th>
                        <th className="px-3 py-2.5 text-right font-bold text-gray-600">Value</th>
                        <th className="px-3 py-2.5 text-center font-bold text-gray-600">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {validSelected.map((si) => (
                        <tr key={si.id || si.billNo}>
                          <td className="px-3 py-2.5 font-mono font-bold text-indigo-600">{si.billNo}</td>
                          <td className="px-3 py-2.5 text-gray-800">{si.partyName}</td>
                          <td className="px-3 py-2.5 font-mono text-gray-600">{si.customerGstin || '—'}</td>
                          <td className="px-3 py-2.5 text-gray-600">{formatDate(si.date)}</td>
                          <td className="px-3 py-2.5 text-right font-bold text-gray-900">{formatINR(si.grandTotal)}</td>
                          <td className="px-3 py-2.5 text-center">
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">
                              ✓ Ready
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <button
                  onClick={() => setStep(1)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                >
                  <i className="ri-arrow-left-line" /> Back to Selection
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={validSelected.length === 0}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-[#4f46e5] rounded-lg hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer shadow-xs transition-colors"
                >
                  Proceed to Register ({validSelected.length}) <i className="ri-arrow-right-line" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Review & Generate */}
          {step === 3 && (
            <div className="space-y-4">
              {!generating && !done && (
                <>
                  <div className="bg-indigo-50/70 border border-indigo-200 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wider mb-2">Final Confirmation</h4>
                    <div className="space-y-1.5 text-xs">
                      <div className="flex justify-between text-indigo-800">
                        <span>Total Invoices to Register:</span>
                        <span className="font-bold">{validSelected.length}</span>
                      </div>
                      <div className="flex justify-between text-indigo-800">
                        <span>Total Batch Taxable Value:</span>
                        <span className="font-bold font-mono">{formatINR(validSelected.reduce((s, si) => s + si.grandTotal, 0))}</span>
                      </div>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                    <div className="max-h-[300px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Invoice No</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Customer</th>
                            <th className="px-3 py-2.5 text-right font-bold text-gray-600">Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {validSelected.map((si) => (
                            <tr key={si.id || si.billNo}>
                              <td className="px-3 py-2.5 font-mono font-bold text-indigo-600">{si.billNo}</td>
                              <td className="px-3 py-2.5 text-gray-700">{si.partyName}</td>
                              <td className="px-3 py-2.5 text-right font-bold text-gray-900">{formatINR(si.grandTotal)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {generating && !done && (
                <div className="text-center py-10 space-y-4">
                  <div className="w-12 h-12 rounded-full bg-indigo-50 border border-indigo-200 flex items-center justify-center mx-auto text-indigo-600">
                    <i className="ri-loader-4-line animate-spin text-2xl" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">
                      Registering with Government IRP ({progress.current} of {progress.total})
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">Please do not close this window while registering...</p>
                  </div>
                  <div className="max-w-md mx-auto bg-gray-200 rounded-full h-2.5 overflow-hidden shadow-inner">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full transition-all duration-300"
                      style={{ width: `${progress.total > 0 ? (progress.current / progress.total) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {done && (
                <div className="space-y-4">
                  <div className={`p-4 rounded-xl border flex items-center gap-3 ${
                    failedCount > 0
                      ? 'bg-amber-50 border-amber-300 text-amber-900'
                      : 'bg-emerald-50 border-emerald-300 text-emerald-900'
                  }`}>
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                      failedCount > 0 ? 'bg-amber-200 text-amber-800' : 'bg-emerald-200 text-emerald-800'
                    }`}>
                      <i className={`text-lg ${failedCount > 0 ? 'ri-error-warning-line' : 'ri-checkbox-circle-line'}`} />
                    </div>
                    <div>
                      <h4 className="text-xs font-bold">
                        Batch Complete: {successCount} Succeeded {failedCount > 0 ? `, ${failedCount} Failed` : ''}
                      </h4>
                      <p className="text-[11px] opacity-90 mt-0.5">
                        IRNs have been saved and linked to sales invoice records.
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden shadow-xs">
                    <div className="max-h-[320px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-slate-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Invoice</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Status</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">IRN / Ack</th>
                            <th className="px-3 py-2.5 text-left font-bold text-gray-600">Details / Error</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {results.map((r, i) => (
                            <tr key={i}>
                              <td className="px-3 py-2.5 font-mono font-bold text-indigo-600">{r.invoice}</td>
                              <td className="px-3 py-2.5">
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold inline-flex items-center gap-1 ${
                                  r.status === 'SUCCESS'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : 'bg-red-50 text-red-600 border border-red-200'
                                }`}>
                                  {r.status === 'SUCCESS' ? '✓ Registered' : '✗ Failed'}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 font-mono text-gray-600">
                                {r.irn ? (
                                  <div className="flex items-center gap-1.5">
                                    <span className="truncate max-w-[140px]" title={r.irn}>{r.irn.slice(0, 18)}...</span>
                                    <button
                                      onClick={() => handleCopyIrn(r.irn)}
                                      title="Copy IRN"
                                      className="text-gray-400 hover:text-indigo-600"
                                    >
                                      <i className="ri-file-copy-line" />
                                    </button>
                                  </div>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td className="px-3 py-2.5 text-gray-600">
                                {r.error ? <span className="text-red-600">{r.error}</span> : <span className="text-emerald-700 font-mono">Ack: {r.ackNumber || 'OK'}</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                {!generating && !done ? (
                  <>
                    <button
                      onClick={() => setStep(2)}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 cursor-pointer transition-colors"
                    >
                      <i className="ri-arrow-left-line" /> Back to Review
                    </button>
                    <button
                      onClick={handleGenerateAll}
                      className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 cursor-pointer shadow-xs transition-colors"
                    >
                      <i className="ri-shield-check-line text-sm" /> Generate All {validSelected.length} IRNs
                    </button>
                  </>
                ) : done ? (
                  <div className="flex justify-end w-full">
                    <button
                      onClick={() => {
                        onSuccess?.();
                        onClose();
                      }}
                      className="inline-flex items-center gap-1.5 px-5 py-2 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 cursor-pointer shadow-xs transition-colors"
                    >
                      Done & View E-Invoices
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}